import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from "@simplewebauthn/browser";
import type { SiteContentData } from "@/data/site";

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...init,
    headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const message = data && typeof data === "object" && "error" in data ? String(data.error) : null;
    throw new Error(message ?? `Request failed (${response.status})`);
  }
  return data as T;
}

export function getWebAuthnStatus(): Promise<{ registered: boolean }> {
  return jsonFetch("/api/webauthn/status");
}

export function getSession(): Promise<{ authenticated: boolean }> {
  return jsonFetch("/api/session");
}

export async function registerPasskey(setupToken: string): Promise<void> {
  const optionsJSON = await jsonFetch<PublicKeyCredentialCreationOptionsJSON>(
    `/api/webauthn/register/options?token=${encodeURIComponent(setupToken)}`,
  );
  const attestation = await startRegistration({ optionsJSON });
  await jsonFetch("/api/webauthn/register/verify", {
    method: "POST",
    body: JSON.stringify(attestation),
  });
}

export async function loginWithPasskey(): Promise<void> {
  const optionsJSON = await jsonFetch<PublicKeyCredentialRequestOptionsJSON>("/api/webauthn/login/options");
  const assertion = await startAuthentication({ optionsJSON });
  await jsonFetch("/api/webauthn/login/verify", {
    method: "POST",
    body: JSON.stringify(assertion),
  });
}

export async function logout(): Promise<void> {
  await fetch("/api/logout", { method: "POST" });
}

export function saveContent(content: SiteContentData): Promise<SiteContentData> {
  return jsonFetch("/api/content", { method: "PUT", body: JSON.stringify(content) });
}
